"use client";

import { c as _c } from "react/compiler-runtime";
import { trapTabFocus } from "@/components/utils/modal/focusTrap";
import type { ApiWave } from "@/generated/models/ApiWave";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { WaveDropCreate } from "./WaveDropCreate";
interface WaveLeaderboardCurationDropModalProps {
  readonly isOpen: boolean;
  readonly wave: ApiWave;
  readonly onClose: () => void;
  readonly initialUrl?: string | null | undefined;
}
export function WaveLeaderboardCurationDropModal(t0) {
  const $ = _c(26);
  const {
    isOpen,
    wave,
    onClose,
    initialUrl: t1
  } = t0;
  const initialUrl = t1 === undefined ? null : t1;
  const canUseDOM = typeof document !== "undefined";
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const previouslyFocusedElementRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== onClose) {
    t2 = () => {
      onCloseRef.current = onClose;
    };
    t3 = [onClose];
    $[0] = onClose;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      const activeElement = document.activeElement;
      previouslyFocusedElementRef.current = activeElement instanceof HTMLElement ? activeElement : null;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const focusInitialTarget = () => {
        const initialTarget = closeButtonRef.current ?? titleRef.current ?? panelRef.current;
        initialTarget?.focus();
      };
      focusInitialTarget();
      const onKeyDown = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          onCloseRef.current();
          return;
        }
        if (event.key !== "Tab") {
          return;
        }
        const panel = panelRef.current;
        if (!panel) {
          return;
        }
        trapTabFocus(event, panel);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener("keydown", onKeyDown);
        const previouslyFocusedElement = previouslyFocusedElementRef.current;
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus();
        }
        previouslyFocusedElementRef.current = null;
      };
    };
    t5 = [isOpen];
    $[3] = isOpen;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  if (!canUseDOM || !isOpen) {
    return null;
  }
  let t6;
  if ($[6] !== onClose) {
    t6 = <button type="button" onClick={onClose} className="tw-fixed tw-inset-0 tw-cursor-default tw-border-0 tw-bg-transparent tw-p-0" aria-label="Close drop artwork modal" tabIndex={-1} />;
    $[6] = onClose;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 12,
      scale: 0.98
    };
    t8 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t9 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2 id="leaderboard-drop-art-title" ref={titleRef} className="tw-mb-0 tw-text-2xl tw-font-bold tw-text-white" tabIndex={-1}>Drop Artwork</h2>;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <XMarkIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== onClose) {
    t12 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-iron-800 tw-p-6">{t10}<button ref={closeButtonRef} type="button" onClick={onClose} className="tw-flex tw-size-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-iron-300 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-100" aria-label="Close modal">{t11}</button></div>;
    $[13] = onClose;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="tw-mb-5 tw-max-w-lg tw-text-base tw-text-iron-400">Enter a supported curation URL to submit a new piece to the leaderboard.</p>;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== initialUrl || $[17] !== onClose || $[18] !== wave) {
    t14 = <div className="tw-max-h-[calc(90vh-88px)] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-px-6 tw-pb-6 tw-pt-5">{t13}<WaveDropCreate wave={wave} onSuccess={onClose} isCurationLeaderboard={true} initialCurationUrl={initialUrl} /></div></div>;
    $[16] = initialUrl;
    $[17] = onClose;
    $[18] = wave;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t12 || $[21] !== t14) {
    t15 = <div className="tw-relative tw-z-10 tw-flex tw-h-full tw-items-start tw-justify-center tw-px-4 tw-pb-4 tw-pt-[calc(env(safe-area-inset-top,0px)+1rem)] lg:tw-items-center"><motion.div ref={panelRef} initial={t7} animate={t8} transition={t9} className="tw-max-h-[90vh] tw-w-full tw-max-w-3xl tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-shadow-2xl" data-testid="curation-drop-modal-panel" tabIndex={-1}>{t12}{t14}</motion.div></div>;
    $[20] = t12;
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t15 || $[24] !== t6) {
    t16 = createPortal(<dialog open={true} aria-modal="true" aria-labelledby="leaderboard-drop-art-title" className="tailwind-scope tw-fixed tw-inset-0 tw-z-[1000] tw-m-0 tw-h-full tw-w-full tw-max-w-none tw-border-0 tw-bg-gray-600/50 tw-p-0 tw-outline-none tw-backdrop-blur-[1px]" data-testid="curation-drop-modal">{t6}{t15}</dialog>, document.body);
    $[23] = t15;
    $[24] = t6;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  return t16;
}